import { Box, Button, CircularProgress, alpha } from '@mui/material';
import {
    type ReactNode,
    createContext,
    memo,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { addItemsToOrderIsCreating } from 'features/AddItemsToOrder/model/selectors/addItemsToOrderSelectors';
import { FilterDropDown, type FilterOption } from 'shared/ui/FilterDropDown';
import { SearchField } from 'shared/ui/SearchField/SearchField';

export interface ListsToolbarState {
    itemSearch: string;
    setItemSearch: (value: string) => void;
    categoryFilter: string;
    setCategoryFilter: (value: string) => void;
    filterOptions: FilterOption[];
    canAddAll: boolean;
    hasActiveList: boolean;
    allItemsOnOrder: boolean;
    isListLoading: boolean;
    onAddAll: () => void;
    onRemoveAll: () => void;
}

const ListsToolbarContext = createContext<{
    state: ListsToolbarState | null;
    setState: (state: ListsToolbarState | null) => void;
} | null>(null);

export const OrderAddItemsListsToolbarProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [state, setState] = useState<ListsToolbarState | null>(null);
    const value = useMemo(() => ({ state, setState }), [state]);
    return (
        <ListsToolbarContext.Provider value={value}>
            {children}
        </ListsToolbarContext.Provider>
    );
};

export const useRegisterListsToolbar = (state: ListsToolbarState | null) => {
    const ctx = useContext(ListsToolbarContext);

    useEffect(() => {
        if (!ctx) {
            return undefined;
        }
        ctx.setState(state);
        return () => ctx.setState(null);
    }, [ctx, state]);
};

export const OrderAddItemsListsToolbar = memo(() => {
    const { t } = useTranslation();
    const isCreatingOrder = useSelector(addItemsToOrderIsCreating);
    const ctx = useContext(ListsToolbarContext);
    const state = ctx?.state;

    if (!state) {
        return <Box sx={{ flex: 1, minWidth: 0 }} />;
    }

    const selectedCategoryIds =
        state.categoryFilter === 'all' ? [] : [state.categoryFilter];

    const isSearchDisabled = !state.hasActiveList;

    return (
        <>
            <Box
                sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    maxWidth: '600px',
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: (theme) =>
                        isSearchDisabled
                            ? alpha(theme.palette.common.black, 0.1)
                            : theme.palette.divider,
                    backgroundColor: isSearchDisabled
                        ? '#F8F8F8'
                        : 'transparent',
                    overflow: 'hidden',
                }}
            >
                <SearchField
                    value={state.itemSearch}
                    setValue={state.setItemSearch}
                    placeholder={t('customLists.searchList')}
                    size="small"
                    hideBorder
                    disabled={isSearchDisabled}
                    data-testclass="order-add-items-list-item-search"
                    sx={{
                        height: '40px',
                        '& .MuiOutlinedInput-root': {
                            height: '40px',
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                        },
                        '& .MuiOutlinedInput-root.Mui-disabled > input': {
                            backgroundColor: 'transparent',
                        },
                    }}
                />
            </Box>
            {state.hasActiveList ? (
                <>
                    <FilterDropDown
                        id="order-add-items-list-category-filter"
                        label={t('Category')}
                        options={state.filterOptions}
                        value={selectedCategoryIds}
                        setValue={(ids) =>
                            state.setCategoryFilter(ids[0] ?? 'all')
                        }
                        selectionMode="single"
                        sx={{
                            flexShrink: 0,
                            width: 'max-content',
                            minWidth: '156px',
                            maxWidth: '45%',
                        }}
                    />
                    <Button
                        variant="contained"
                        disabled={
                            isCreatingOrder ||
                            (!state.allItemsOnOrder && !state.canAddAll)
                        }
                        aria-busy={state.isListLoading || isCreatingOrder}
                        onClick={() => {
                            if (state.isListLoading || isCreatingOrder) {
                                return;
                            }

                            if (state.allItemsOnOrder) {
                                state.onRemoveAll();
                                return;
                            }

                            state.onAddAll();
                        }}
                        data-testclass="order-add-items-add-all-items"
                        sx={{
                            flexShrink: 0,
                            marginLeft: 'auto',
                            textTransform: 'none',
                            height: '40px',
                            minWidth: '156px',
                        }}
                    >
                        {state.isListLoading ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : state.allItemsOnOrder ? (
                            t('customLists.removeAllItems')
                        ) : (
                            t('customLists.addAllItems')
                        )}
                    </Button>
                </>
            ) : null}
        </>
    );
});

OrderAddItemsListsToolbar.displayName = 'OrderAddItemsListsToolbar';
